import { PhysioLayout } from "@/components/layout/physio-layout";
import { Waves, CheckCircle2, XCircle, AlertTriangle, Clock, Thermometer } from "lucide-react";

const INDICATIONS = [
  "Inflammatory arthritis with active joint disease limiting land-based exercise",
  "Severe pain and muscle spasm inhibiting gym-based rehabilitation",
  "Post-surgical rehabilitation where reduced weight-bearing is required",
  "axSpA — particularly beneficial for thoracic mobility and global exercise",
  "RA with generalised joint involvement limiting land-based weight-bearing",
  "SLE — warm water reduces joint pain; buoyancy supports low-impact exercise",
  "Osteoporosis — hydrotherapy provides safe exercise environment",
  "Significant deconditioning where land exercise is not tolerated",
];

const CONTRAINDICATIONS_ABSOLUTE = [
  "Open wounds, skin ulcers, or active digital ulcers (SSc)",
  "Urinary or faecal incontinence (uncontrolled)",
  "Current upper respiratory tract infection or febrile illness",
  "Severe cardiovascular disease (uncontrolled hypertension, recent MI within 3 months, severe HF)",
  "Epilepsy (unless individually risk-assessed with additional safety measures)",
  "Severe chlorine/chemical sensitivity or allergy",
  "Active infectious disease (TB, hepatitis, MRSA — check local pool protocols)",
  "Fear of water (unless desensitisation programme planned)",
];

const CONTRAINDICATIONS_RELATIVE = [
  "Tracheostomy or colostomy — requires specialist assessment and waterproof covering",
  "Severe immunosuppression — check local policy; mask may be required",
  "Raynaud's phenomenon — water temperature must be ≥ 35°C; transition carefully",
  "Pulmonary arterial hypertension — individual cardiac assessment required",
  "ILD with FVC < 50% — assess SpO2 during exercise; poolside SpO2 monitoring",
  "Severe osteoporosis — supervise transfers carefully; pool entry and exit",
  "Photosensitive skin conditions (some SLE patients) — UV-protected indoor pool preferred",
];

const SESSIONS = [
  {
    condition: "Rheumatoid Arthritis (RA)",
    duration: "30–45 minutes",
    temperature: "35–36°C",
    structure: [
      { phase: "Warm-Up (5–8 min)", activities: ["Walking in chest-deep water", "Gentle arm swings", "Shoulder circles forward and back", "Hip circles"] },
      { phase: "Main Exercise (20–30 min)", activities: ["Walking forwards, backwards, and sideways", "Knee lifts with float support", "Wall push-ups (bilateral or unilateral)", "Hip abduction/adduction with buoyancy aid", "Finger flexion/extension in warm water", "Theraband resistance (pool-safe)", "Aerobic intervals: water jogging 30s/rest 30s"] },
      { phase: "Cool-Down (5–8 min)", activities: ["Slow walking", "Guided stretching using pool wall", "Shoulder and wrist stretches", "Relaxation float if tolerated"] },
    ],
    notes: "Monitor joint pain and swelling post-session. Reduce intensity if inflammatory flare. Hand exercises particularly effective in warm water (37°C hand basin alternative for digital exercises).",
  },
  {
    condition: "Axial Spondyloarthritis (axSpA)",
    duration: "30–45 minutes",
    temperature: "33–35°C",
    structure: [
      { phase: "Warm-Up (5–8 min)", activities: ["Forward walking and backwards walking", "Cervical rotation in standing", "Gentle thoracic rotation", "Hip mobility circles"] },
      { phase: "Main Exercise (20–30 min)", activities: ["Thoracic extension: lean against pool wall", "Chest expansion breathing exercises", "Horizontal floating: thoracic extension over noodle", "Spinal rotation in waist-deep water", "Hip extension exercises: lean on wall, kick back", "Aerobic walking intervals", "Supine float: encourage spinal extension position"] },
      { phase: "Cool-Down (5–8 min)", activities: ["Slow walking", "Lateral thoracic stretches", "Prone float (if patient can manage)", "Diaphragmatic breathing"] },
    ],
    notes: "Emphasise extension over flexion. Prone floating particularly therapeutic for kyphosis prevention. Build to 3×/week alongside daily BASEX home programme.",
  },
  {
    condition: "Systemic Lupus Erythematosus (SLE)",
    duration: "20–30 minutes (fatigue-adapted)",
    temperature: "34–35°C (avoid heat intolerance triggers)",
    structure: [
      { phase: "Warm-Up (4–5 min)", activities: ["Gentle walking in waist-deep water", "Arm swings", "Ankle mobility", "Breathing exercises"] },
      { phase: "Main Exercise (15–20 min)", activities: ["Low-intensity walking programme (pacing)", "Supported aerobic exercise using floats", "Gentle resistance exercises with bands", "Range of movement exercises for affected joints", "Relaxation techniques in water"] },
      { phase: "Cool-Down (5 min)", activities: ["Slow walking", "Float relaxation", "Gentle stretching"] },
    ],
    notes: "Use energy envelope model — do not exceed 70–80% of energy budget. Avoid overheating: ensure pool temperature does not exceed 35°C. Monitor for post-exertional malaise 24–48h after. Indoor UV-protected pool preferred for photosensitive patients.",
  },
];

const EVIDENCE = [
  { source: "NICE NG65 (Spondyloarthritis)", summary: "Recommends supervised exercise including hydrotherapy as part of axSpA management. Exercise reduces disease activity and improves function." },
  { source: "BSR/BHPR RA Guidelines", summary: "Hydrotherapy is recommended as an adjunct to land-based rehabilitation in RA, particularly for severely affected patients with multiple joint involvement." },
  { source: "CSP (Chartered Society of Physiotherapy)", summary: "CSP supports aquatic physiotherapy as a core physiotherapy modality with robust evidence for inflammatory arthritis." },
  { source: "Cochrane Review: Aquatic exercise for rheumatoid arthritis (2011)", summary: "Aquatic exercise has short-term beneficial effects on patient wellbeing, physical function, and disease activity in RA. Effect sizes small to moderate." },
  { source: "Eversden et al. (2007) axSpA RCT", summary: "Hydrotherapy significantly improved BASDAI and BASFI over land exercise at 3 months; effects maintained at 1 year when continued." },
  { source: "ACPR (Association of Chartered Physiotherapists in Rheumatology)", summary: "ACPR supports aquatic physiotherapy as a specific competency within rheumatology physiotherapy practice with condition-specific safety guidance." },
];

export default function Hydrotherapy() {
  return (
    <PhysioLayout title="Hydrotherapy">
      <div className="space-y-8">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <Waves className="h-6 w-6 text-blue-600" />
            <h2 className="font-bold text-slate-900">Aquatic Physiotherapy in Rheumatology</h2>
          </div>
          <p className="text-sm text-slate-600">
            Hydrotherapy exploits the physical properties of water — buoyancy, viscosity, hydrostatic pressure, and warmth — to facilitate exercise in patients where land-based rehabilitation is limited by pain, joint damage, or deconditioning. Temperature guidance: <strong>33–36°C</strong> for most rheumatology conditions (warmer than standard therapeutic pools but lower than spa/jacuzzi temperature).
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <h3 className="font-bold text-slate-900">Indications</h3>
            </div>
            <ul className="space-y-2">
              {INDICATIONS.map((ind, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 mt-2" />
                  {ind}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="h-5 w-5 text-red-500" />
                <h3 className="font-bold text-slate-900">Absolute Contraindications</h3>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                <ul className="space-y-1.5">
                  {CONTRAINDICATIONS_ABSOLUTE.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-red-900">
                      <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <h3 className="font-bold text-slate-900">Relative Contraindications</h3>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <ul className="space-y-1.5">
                  {CONTRAINDICATIONS_RELATIVE.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Sample Session Structures
          </h3>
          <div className="space-y-6">
            {SESSIONS.map((session) => (
              <div key={session.condition} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <h4 className="font-bold text-slate-900">{session.condition}</h4>
                  <span className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-full">
                    <Clock className="h-3 w-3" /> {session.duration}
                  </span>
                  <span className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-1 rounded-full">
                    <Thermometer className="h-3 w-3" /> {session.temperature}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {session.structure.map((phase) => (
                    <div key={phase.phase} className="bg-slate-50 rounded-xl p-4">
                      <h5 className="font-semibold text-slate-800 text-sm mb-2">{phase.phase}</h5>
                      <ul className="space-y-1">
                        {phase.activities.map((act, i) => (
                          <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <span className="text-blue-400 shrink-0">·</span> {act}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <p className="text-xs text-blue-800"><strong>Clinical Notes:</strong> {session.notes}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Evidence Base</h3>
          <div className="space-y-3">
            {EVIDENCE.map((ev, i) => (
              <div key={i} className="border-b border-slate-100 last:border-0 pb-3 last:pb-0">
                <p className="text-sm font-semibold text-slate-800">{ev.source}</p>
                <p className="text-sm text-slate-600 mt-0.5">{ev.summary}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-slate-900 mb-1">Safety Reminders</h4>
              <ul className="text-sm text-amber-900 space-y-1">
                <li>• Always complete a full pre-hydrotherapy assessment and document contraindication screening</li>
                <li>• Ensure poolside emergency equipment (AED, rescue equipment) and trained supervision at all times</li>
                <li>• Pool entry and exit are high-risk for falls — use appropriate equipment (hoists, steps)</li>
                <li>• Monitor for overheating: ensure patient can communicate throughout session</li>
                <li>• Post-session: adequate rest area and fluid replacement; monitor for delayed fatigue</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
